import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import {
  Settings,
  Shield,
  Globe,
  Save,
  CheckCircle,
  AlertCircle,
  Loader2,
  ImageIcon,
  Upload,
  RotateCcw,
  Share2,
  Facebook,
  Instagram,
  Twitter
} from 'lucide-react';
import { toast } from 'sonner';
import { getFacturamaConfig, updateFacturamaConfig, getPlaceholderConfig, updatePlaceholderConfig, uploadProductImage, getSocialLinks, updateSocialLinks } from '../../services/api';
import { PLACEHOLDER_IMAGES, initPlaceholderImages } from '../../utils/constants';

const SuperAdminConfiguracion = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [environment, setEnvironment] = useState('sandbox');
  const [isConfigured, setIsConfigured] = useState(false);

  // Placeholder images state
  const DEFAULT_PRODUCT_PLACEHOLDER = 'https://images.unsplash.com/photo-1630448926286-0a77c8ba11e4?w=400&h=400&fit=crop&q=80';
  const [placeholderUrl, setPlaceholderUrl] = useState(null);
  const [savingPlaceholder, setSavingPlaceholder] = useState(false);
  const [uploadingPlaceholder, setUploadingPlaceholder] = useState(false);

  // Social links state
  const [socialLinks, setSocialLinks] = useState({ facebook: '', instagram: '', twitter: '', tiktok: '' });
  const [savingSocial, setSavingSocial] = useState(false);

  useEffect(() => {
    loadConfig();
    loadPlaceholderConfig();
    loadSocialLinks();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await getFacturamaConfig();
      const data = response.data;
      setEnvironment(data.environment || 'sandbox');
      setIsConfigured(data.configured);
    } catch (error) {
      console.error('Error loading Facturama config:', error);
      toast.error('Error al cargar configuración de Facturama');
    } finally {
      setLoading(false);
    }
  };

  const loadPlaceholderConfig = async () => {
    try {
      const response = await getPlaceholderConfig();
      setPlaceholderUrl(response.data.product_url || null);
    } catch (error) {
      console.error('Error loading placeholder config:', error);
    }
  };

  const handlePlaceholderUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPlaceholder(true);
    try {
      const response = await uploadProductImage(file);
      const url = response.data.image_url;
      setPlaceholderUrl(url);
      toast.success('Imagen subida. Presiona Guardar para aplicar.');
    } catch (error) {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingPlaceholder(false);
    }
  };

  const handleSavePlaceholder = async () => {
    setSavingPlaceholder(true);
    try {
      await updatePlaceholderConfig({ product_url: placeholderUrl });
      initPlaceholderImages({ product_url: placeholderUrl });
      toast.success('Placeholder actualizado correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar placeholder');
    } finally {
      setSavingPlaceholder(false);
    }
  };

  const handleRestoreDefault = async () => {
    setSavingPlaceholder(true);
    try {
      await updatePlaceholderConfig({ product_url: null });
      setPlaceholderUrl(null);
      initPlaceholderImages({ product_url: null });
      PLACEHOLDER_IMAGES.product = DEFAULT_PRODUCT_PLACEHOLDER;
      toast.success('Placeholder restaurado al valor por defecto');
    } catch (error) {
      toast.error('Error al restaurar placeholder');
    } finally {
      setSavingPlaceholder(false);
    }
  };

  const loadSocialLinks = async () => {
    try {
      const response = await getSocialLinks();
      setSocialLinks({
        facebook: response.data.facebook || '',
        instagram: response.data.instagram || '',
        twitter: response.data.twitter || '',
        tiktok: response.data.tiktok || ''
      });
    } catch (error) {
      console.error('Error loading social links:', error);
    }
  };

  const handleSaveSocial = async () => {
    setSavingSocial(true);
    try {
      await updateSocialLinks({
        facebook: socialLinks.facebook || null,
        instagram: socialLinks.instagram || null,
        twitter: socialLinks.twitter || null,
        tiktok: socialLinks.tiktok || null
      });
      toast.success('Redes sociales guardadas correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar redes sociales');
    } finally {
      setSavingSocial(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateFacturamaConfig({ environment });
      toast.success(response.data.message || 'Configuración guardada');
      setIsConfigured(true);
    } catch (error) {
      console.error('Error saving Facturama config:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const isProduction = environment === 'production';
  const apiUrl = isProduction
    ? 'https://api.facturama.mx'
    : 'https://apisandbox.facturama.mx';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Configuración del Sistema</h1>
          <p className="text-slate-600 mt-2">
            Ajustes globales de la plataforma
          </p>
        </div>

        {/* Facturama Environment Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Globe className="w-6 h-6 text-slate-600" />
            API Facturama — Entorno
          </h2>

          {/* Status Badge */}
          <div className={`p-4 rounded-xl border mb-6 ${
            isProduction
              ? 'bg-green-50 border-green-200'
              : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex items-center gap-3">
              {isProduction ? (
                <>
                  <Shield className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">Modo Producción</p>
                    <p className="text-sm text-green-700">Las facturas se timbran con validez fiscal real</p>
                  </div>
                </>
              ) : (
                <>
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-semibold text-amber-800">Modo Sandbox</p>
                    <p className="text-sm text-amber-700">Las facturas son de prueba, sin validez fiscal</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Radio Options */}
          <div className="space-y-3 mb-6">
            <label
              className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                environment === 'sandbox'
                  ? 'border-amber-400 bg-amber-50'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <input
                type="radio"
                name="facturama_env"
                value="sandbox"
                checked={environment === 'sandbox'}
                onChange={() => setEnvironment('sandbox')}
                className="w-4 h-4 text-amber-600 focus:ring-amber-500"
              />
              <div className="flex-1">
                <p className="font-semibold text-slate-900">Sandbox (Pruebas)</p>
                <p className="text-sm text-slate-500">
                  apisandbox.facturama.mx — Para desarrollo y pruebas
                </p>
              </div>
              <span className="px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                Sandbox
              </span>
            </label>

            <label
              className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                environment === 'production'
                  ? 'border-green-400 bg-green-50'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <input
                type="radio"
                name="facturama_env"
                value="production"
                checked={environment === 'production'}
                onChange={() => setEnvironment('production')}
                className="w-4 h-4 text-green-600 focus:ring-green-500"
              />
              <div className="flex-1">
                <p className="font-semibold text-slate-900">Producción</p>
                <p className="text-sm text-slate-500">
                  api.facturama.mx — Facturas con validez fiscal real
                </p>
              </div>
              <span className="px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                Producción
              </span>
            </label>
          </div>

          {/* Current API URL */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-slate-700 mb-1">URL del API actual</p>
            <code className="text-sm text-slate-900 bg-slate-200 px-2 py-1 rounded">
              {apiUrl}
            </code>
          </div>

          {/* Warning for production */}
          {environment === 'production' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">
                <strong>Atención:</strong> En modo producción, todas las facturas emitidas tendrán
                validez fiscal ante el SAT. Asegúrate de que los CSD y datos fiscales sean correctos.
              </p>
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </button>
        </div>

        {/* Placeholder Image Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <ImageIcon className="w-6 h-6 text-slate-600" />
            Imagen Placeholder de Productos
          </h2>

          <p className="text-sm text-slate-500 mb-4">
            Esta imagen se muestra cuando un producto no tiene foto. Se aplica en todo el marketplace y dashboard.
          </p>

          {/* Preview */}
          <div className="flex items-center gap-6 mb-6">
            <img
              src={placeholderUrl || DEFAULT_PRODUCT_PLACEHOLDER}
              alt="Placeholder actual"
              className="w-[200px] h-[200px] object-cover rounded-lg border border-slate-200"
            />
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">
                {placeholderUrl ? 'Imagen personalizada' : 'Imagen por defecto (Unsplash)'}
              </p>
              <p className="text-xs text-slate-400 break-all max-w-xs">
                {placeholderUrl || DEFAULT_PRODUCT_PLACEHOLDER}
              </p>
            </div>
          </div>

          {/* Upload Button */}
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 font-medium rounded-lg hover:bg-slate-200 cursor-pointer transition-colors">
              {uploadingPlaceholder ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {uploadingPlaceholder ? 'Subiendo...' : 'Subir imagen'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePlaceholderUpload}
                disabled={uploadingPlaceholder}
              />
            </label>

            <button
              onClick={handleSavePlaceholder}
              disabled={savingPlaceholder}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >
              {savingPlaceholder ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Guardar
            </button>

            {placeholderUrl && (
              <button
                onClick={handleRestoreDefault}
                disabled={savingPlaceholder}
                className="flex items-center gap-2 px-4 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Restaurar default
              </button>
            )}
          </div>
        </div>

        {/* Social Links Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Share2 className="w-6 h-6 text-slate-600" />
            Redes Sociales
          </h2>

          <p className="text-sm text-slate-500 mb-6">
            URLs de las redes sociales que aparecen en el footer del marketplace. Deja vacío para ocultar.
          </p>

          <div className="space-y-4 mb-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                <Facebook className="w-4 h-4 text-blue-600" />
                Facebook
              </label>
              <input
                type="url"
                value={socialLinks.facebook}
                onChange={(e) => setSocialLinks({ ...socialLinks, facebook: e.target.value })}
                placeholder="https://facebook.com/tu-pagina"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                <Instagram className="w-4 h-4 text-pink-600" />
                Instagram
              </label>
              <input
                type="url"
                value={socialLinks.instagram}
                onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                placeholder="https://instagram.com/tu-cuenta"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                <Twitter className="w-4 h-4 text-sky-500" />
                X (Twitter)
              </label>
              <input
                type="url"
                value={socialLinks.twitter}
                onChange={(e) => setSocialLinks({ ...socialLinks, twitter: e.target.value })}
                placeholder="https://x.com/tu-cuenta"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.46a8.18 8.18 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.11z"/>
                </svg>
                TikTok
              </label>
              <input
                type="url"
                value={socialLinks.tiktok}
                onChange={(e) => setSocialLinks({ ...socialLinks, tiktok: e.target.value })}
                placeholder="https://tiktok.com/@tu-cuenta"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
          </div>

          <button
            onClick={handleSaveSocial}
            disabled={savingSocial}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
          >
            {savingSocial ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {savingSocial ? 'Guardando...' : 'Guardar Redes Sociales'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminConfiguracion;
